import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, QrCode, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

function loadImg(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 2) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.slice(0, maxLines).forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return Math.min(lines.length, maxLines);
}

async function buildCard() {
  const W = 651;
  const H = 1004;
  const S = 2;
  const canvas = document.createElement("canvas");
  canvas.width = W * S;
  canvas.height = H * S;
  const ctx = canvas.getContext("2d");
  ctx.scale(S, S);

  try {
    await document.fonts.ready;
  } catch {
    /* fuentes de respaldo */
  }

  // Fondo: degradado guinda -> gris oscuro
  const g = ctx.createLinearGradient(0, 0, W * 0.35, H);
  g.addColorStop(0, "#C03C54");
  g.addColorStop(1, "#5A524A");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Textura Flor Tének muy sutil
  try {
    const tex = await loadImg("/textura-flor-tenek.webp");
    ctx.save();
    ctx.globalAlpha = 0.05;
    const scale = Math.max(W / tex.width, H / tex.height);
    ctx.drawImage(tex, (W - tex.width * scale) / 2, (H - tex.height * scale) / 2, tex.width * scale, tex.height * scale);
    ctx.restore();
  } catch {
    /* textura opcional */
  }

  // QR grande: fondo 100% transparente, módulos blancos, pequeño margen respecto al marco arena
  const qrCanvas = document.getElementById("qr-canvas-download");
  const qs = 520;
  const qx = (W - qs) / 2;
  const qy = 44;
  if (qrCanvas) {
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrCanvas, qx, qy, qs, qs);
    ctx.restore();
  }

  // Frase al centro: misma fuente del hero (Playfair Display itálica), estilo párrafo
  ctx.textAlign = "center";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "italic 600 38px 'Playfair Display', Georgia, serif";
  wrapText(ctx, "Tampico, dos años de transformación real", W / 2, 648, W - 170, 50, 2);

  // Logo del evento centrado abajo, más grande
  try {
    const logo = await loadImg("/logo-hero.webp");
    const lh = 176;
    const lw = (logo.width / logo.height) * lh;
    ctx.drawImage(logo, (W - lw) / 2, 764, lw, lh);
  } catch {
    /* logo opcional */
  }

  // Borde perimetral más fino y preciso en arena
  ctx.strokeStyle = "#BEB195";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(16, 16, W - 32, H - 32, 16);
  ctx.stroke();

  return canvas;
}

export default function QrModal({ open }) {
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const [preview, setPreview] = useState("");
  const [cardCanvas, setCardCanvas] = useState(null);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBuilding(true);
    const t = setTimeout(() => {
      buildCard()
        .then((c) => {
          if (!alive) return;
          setCardCanvas(c);
          setPreview(c.toDataURL("image/png"));
        })
        .catch(() => alive && toast.error("No se pudo generar la tarjeta"))
        .finally(() => alive && setBuilding(false));
    }, 80);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const downloadCard = () => {
    if (!cardCanvas) return;
    const a = document.createElement("a");
    a.href = cardCanvas.toDataURL("image/png");
    a.download = "tarjeta-segundo-informe-tampico.png";
    a.click();
    toast.success("Tarjeta descargada en PNG · alta resolución (300 dpi)");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="qr-panel"
            className="pointer-events-auto w-[400px] max-w-full rounded-3xl border border-arena/70 bg-arena-sand/80 backdrop-blur-xl shadow-[0_30px_60px_-20px_rgba(45,40,37,0.5)] p-5"
          >
          <div className="flex items-center justify-center gap-2.5 mb-3">
            <span className="p-2 rounded-xl bg-guinda/10 text-guinda">
              <QrCode className="w-4 h-4" aria-hidden="true" />
            </span>
            <p className="font-serif font-semibold text-gris-charcoal text-base leading-tight">
              Escanea para acceder al sitio
            </p>
          </div>

          {preview ? (
            <img
              src={preview}
              alt="Vista previa de la tarjeta con QR del Segundo Informe de Gobierno"
              data-testid="qr-card-preview"
              className="mx-auto w-auto max-w-full max-h-[54vh] rounded-2xl border border-border shadow-[0_16px_34px_-16px_rgba(90,82,74,0.5)]"
            />
          ) : (
            <div
              data-testid="qr-card-loading"
              className="w-full aspect-[651/1004] rounded-2xl bg-white/70 border border-border flex items-center justify-center gap-2 text-gris text-sm"
            >
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Generando tarjeta…
            </div>
          )}

          <p data-testid="qr-modal-url" className="mt-3 font-mono text-[11px] text-gris break-all text-center">
            {siteUrl}
          </p>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={copyLink}
              data-testid="qr-copy-button"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-guinda text-guinda hover:bg-guinda hover:text-white text-xs font-medium px-4 py-2.5 transition-colors duration-200"
            >
              <Copy className="w-3.5 h-3.5" aria-hidden="true" />
              Copiar enlace
            </button>
            <button
              type="button"
              onClick={downloadCard}
              disabled={building || !cardCanvas}
              data-testid="qr-download-button"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-guinda hover:bg-guinda-dark text-white text-xs font-medium px-4 py-2.5 transition-colors duration-200 disabled:opacity-60"
            >
              <Download className="w-3.5 h-3.5" aria-hidden="true" />
              Descargar PNG
            </button>
          </div>

          <div className="hidden" aria-hidden="true">
            <QRCodeCanvas
              id="qr-canvas-download"
              value={siteUrl || "https://monica-zacil.preview.emergentagent.com"}
              size={1024}
              bgColor="transparent"
              fgColor="#FFFFFF"
              level="H"
            />
          </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
